/*
 * Emote pose renderer.
 *
 * One Graphics overlay per emote, drawn ABOVE the critter's body but BELOW the
 * hat. The critter view holds one instance per critter and toggles which pose
 * is visible / animated each frame.
 *
 * Cozy-frame: no big motion lines, no exclamation bursts. The "wave" is a small
 * arm raised by the ear; "dance" is a slight side-to-side body lean; "sit" is
 * a small grounded blob under the body suggesting the critter is settled.
 *
 * All math here is in critter-local coordinates (origin at body center, -y is up).
 */

import { Container, Graphics } from 'pixi.js';
import type { EmoteName } from '../../../shared/protocol';

export interface EmoteView {
  /** Add to critter container above body, below hat. */
  container: Container;
  /** Set the current emote (or null to clear). */
  setEmote(name: EmoteName | null): void;
  /** Per-frame update (drives the animation phase). */
  update(t: number): void;
}

export function createEmoteView(): EmoteView {
  const root = new Container();
  // We use one Graphics; redraw each frame when active.
  const g = new Graphics();
  root.addChild(g);

  let current: EmoteName | null = null;

  function setEmote(name: EmoteName | null): void {
    current = name;
    if (name === null) {
      g.clear();
      root.visible = false;
    } else {
      root.visible = true;
    }
  }

  function update(t: number): void {
    if (current === null) return;
    g.clear();
    switch (current) {
      case 'wave': {
        // Small arm raised above the right shoulder, animating side-to-side.
        const swing = Math.sin(t * 0.012) * 3;
        const ax = 11 + swing;
        const ay = -6;
        // Arm line (from shoulder to hand).
        g.moveTo(8, -1)
          .lineTo(ax, ay)
          .stroke({ color: 0x3a2820, width: 1.6, cap: 'round' });
        // Hand dot.
        g.circle(ax, ay, 1.6).fill(0x3a2820);
        // Soft motion arc.
        g.moveTo(ax - 3, ay - 1)
          .quadraticCurveTo(ax, ay - 3, ax + 3, ay - 1)
          .stroke({ color: 0xfff4dc, alpha: 0.5, width: 0.6 });
        break;
      }
      case 'dance': {
        // Two tiny musical notes drifting up + the critter root will apply a
        // sway via the parent container (handled in critter.ts updates).
        const phase = t * 0.008;
        const yA = -16 + Math.sin(phase) * 1.5;
        const yB = -18 + Math.sin(phase + 1.2) * 1.5;
        // Note 1.
        g.circle(-7, yA, 1.4).fill(0xffe9a8);
        g.moveTo(-6, yA - 1)
          .lineTo(-6, yA - 5)
          .stroke({ color: 0xffe9a8, width: 0.9 });
        // Note 2.
        g.circle(8, yB, 1.2).fill(0xfff4dc);
        g.moveTo(9, yB - 1)
          .lineTo(9, yB - 4)
          .stroke({ color: 0xfff4dc, width: 0.9 });
        break;
      }
      case 'sit': {
        // Small grounded cushion under the critter — suggests the critter is
        // settled. Drawn at world-y +18 so it sits at ground level.
        g.ellipse(0, 19, 12, 3.5).fill({ color: 0x3a2820, alpha: 0.45 });
        g.ellipse(-3, 19, 5, 1.8).fill({ color: 0xffffff, alpha: 0.15 });
        break;
      }
    }
  }

  return { container: root, setEmote, update };
}

/** Per-emote body-position offset (applied by critter view to its root container). */
export function emoteBodyOffset(name: EmoteName | null, t: number): { x: number; y: number } {
  if (name === 'dance') {
    return { x: Math.sin(t * 0.008) * 2, y: 0 };
  }
  if (name === 'sit') {
    // Sink a bit so the critter feels grounded.
    return { x: 0, y: 4 };
  }
  return { x: 0, y: 0 };
}
